import { useState, useRef } from "react";
import { head } from "lodash";
import { toast } from "react-toastify";
import { deleteCampaignMedia } from "../../../services/campaigns.api";
import { i18n } from "../../../translate/i18n";

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const truncateFileName = (fileName, maxLength = 50) => {
  if (fileName.length <= maxLength) return fileName;
  const extension = fileName.substring(fileName.lastIndexOf('.'));
  const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
  const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 3) + '...';
  return truncatedName + extension;
};

const getFileType = (fileName) => {
  const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
  const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];
  const audioExtensions = ['.mp3', '.ogg', '.wav', '.m4a'];
  const documentExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv'];

  if (imageExtensions.includes(extension)) return { type: 'image', maxSize: 10 * 1024 * 1024, typeName: 'imagen' };
  if (videoExtensions.includes(extension)) return { type: 'video', maxSize: 15 * 1024 * 1024, typeName: 'video' };
  if (audioExtensions.includes(extension)) return { type: 'audio', maxSize: 10 * 1024 * 1024, typeName: 'audio' };
  if (documentExtensions.includes(extension)) return { type: 'document', maxSize: 15 * 1024 * 1024, typeName: 'documento' };
  return null;
};

const useCampaignAttachment = ({ campaign, setCampaign }) => {
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);
  const [isFileListSelected, setIsFileListSelected] = useState(false);
  const [isAttachmentSelected, setIsAttachmentSelected] = useState(false);
  const [formatInvalidModalOpen, setFormatInvalidModalOpen] = useState(false);
  const [formatInvalidMessage, setFormatInvalidMessage] = useState("");
  const [sizeExceededModalOpen, setSizeExceededModalOpen] = useState(false);
  const [sizeExceededMessage, setSizeExceededMessage] = useState("");
  const [attachmentInfo, setAttachmentInfo] = useState(null);

  const handleAttachFile = () => {
    setIsAttachmentSelected(true);
    setIsFileListSelected(false);
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      const fileType = getFileType(file.name);
      if (!fileType) {
        const allowedFormats = {
          images: '.jpg, .jpeg, .png, .gif, .webp',
          videos: '.mp4, .mov, .avi, .mkv, .webm',
          audios: '.mp3, .ogg, .wav, .m4a',
          documents: '.pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt, .csv',
        };
        setFormatInvalidMessage(
          `El formato del archivo "${file.name}" no está permitido.\n\nFormatos permitidos:\n` +
          `• Imágenes: ${allowedFormats.images}\n` +
          `• Videos: ${allowedFormats.videos}\n` +
          `• Audios: ${allowedFormats.audios}\n` +
          `• Documentos: ${allowedFormats.documents}`
        );
        setFormatInvalidModalOpen(true);
        if (attachmentFile.current) {
          attachmentFile.current.value = null;
        }
        return;
      }

      if (file.size > fileType.maxSize) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        const maxSizeMB = (fileType.maxSize / (1024 * 1024)).toFixed(0);
        setSizeExceededMessage(
          `El ${fileType.typeName} "${file.name}" excede el tamaño máximo permitido.\n\n` +
          `Tamaño actual: ${fileSizeMB} MB\n` +
          `Tamaño máximo permitido: ${maxSizeMB} MB`
        );
        setSizeExceededModalOpen(true);
        if (attachmentFile.current) {
          attachmentFile.current.value = null;
        }
        return;
      }

      setAttachment(file);
      setIsAttachmentSelected(true);
      setAttachmentInfo({
        name: truncateFileName(file.name),
        fullName: file.name,
        size: formatFileSize(file.size),
        sizeBytes: file.size,
      });
    }
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
      setIsAttachmentSelected(false);
      setAttachmentInfo(null);
    }

    if (campaign.mediaPath) {
      await deleteCampaignMedia(campaign.id);
      setCampaign((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("campaigns.toasts.deleted"));
    }
  };

  return {
    attachment,
    setAttachment,
    attachmentFile,
    isFileListSelected,
    setIsFileListSelected,
    isAttachmentSelected,
    setIsAttachmentSelected,
    formatInvalidModalOpen,
    setFormatInvalidModalOpen,
    formatInvalidMessage,
    sizeExceededModalOpen,
    setSizeExceededModalOpen,
    sizeExceededMessage,
    attachmentInfo,
    setAttachmentInfo,
    handleAttachFile,
    handleAttachmentFile,
    deleteMedia,
  };
};

export default useCampaignAttachment;
